import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TemplateLanguage, TranslationEntry } from '../../../api/hms';
import {
  acceptSuggestion,
  type ChecklistDraftInput,
  type ChecklistEditor,
  createChecklist,
  discardChecklistDraft,
  getChecklist,
  getChecklists,
  getPublishedChecklists,
  publishChecklist,
  rejectSuggestion,
  saveChecklistDraft,
  saveChecklistLanguages,
  saveChecklistTranslations,
  setChecklistArchived,
  suggestChecklistTranslations,
} from '../../../api/safetyRounds';

export const checklistKeys = {
  all: ['safety-rounds', 'templates'] as const,
  published: ['safety-rounds', 'templates', 'published'] as const,
  one: (id: number) => ['safety-rounds', 'templates', id] as const,
};

export const useChecklistsQuery = () => useQuery({ queryKey: checklistKeys.all, queryFn: getChecklists });

export const usePublishedChecklistsQuery = () =>
  useQuery({ queryKey: checklistKeys.published, queryFn: getPublishedChecklists });

export const useChecklistQuery = (id: number) =>
  useQuery({ queryKey: checklistKeys.one(id), queryFn: () => getChecklist(id) });

export const useCreateChecklistMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createChecklist,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: checklistKeys.all }),
  });
};

/** Every change responds with the editor, so the page updates without fetching again */
const useEditorMutation = <T>(id: number, mutationFn: (input: T) => Promise<ChecklistEditor>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (editor) => {
      queryClient.setQueryData(checklistKeys.one(id), editor);
      queryClient.invalidateQueries({ queryKey: checklistKeys.all, exact: true });
      queryClient.invalidateQueries({ queryKey: checklistKeys.published });
    },
  });
};

export const useSaveChecklistDraftMutation = (id: number) =>
  useEditorMutation(id, (draft: ChecklistDraftInput) => saveChecklistDraft(id, draft));
export const useDiscardChecklistDraftMutation = (id: number) =>
  useEditorMutation(id, () => discardChecklistDraft(id));
export const useSaveChecklistLanguagesMutation = (id: number) =>
  useEditorMutation(id, (languages: TemplateLanguage[]) => saveChecklistLanguages(id, languages));
export const useSaveChecklistTranslationsMutation = (id: number) =>
  useEditorMutation(id, ({ language, entries }: { language: string; entries: TranslationEntry[] }) =>
    saveChecklistTranslations(id, language, entries),
  );
export const useSuggestChecklistTranslationsMutation = (id: number) =>
  useEditorMutation(id, (language: string) => suggestChecklistTranslations(id, language));
export const usePublishChecklistMutation = (id: number) =>
  useEditorMutation(id, (input: { changeNote: string; notifySafetyReps: boolean }) => publishChecklist(id, input));
export const useSetChecklistArchivedMutation = (id: number) =>
  useEditorMutation(id, (archived: boolean) => setChecklistArchived(id, archived));
export const useAcceptSuggestionMutation = (id: number) =>
  useEditorMutation(id, (suggestionId: number) => acceptSuggestion(suggestionId));
export const useRejectSuggestionMutation = (id: number) =>
  useEditorMutation(id, ({ suggestionId, comment }: { suggestionId: number; comment: string }) =>
    rejectSuggestion(suggestionId, comment),
  );
